// Copyright (c) 2019-2023 Cigent Technology Inc. All Rights Reserved.
import { Component, Inject, OnInit } from '@angular/core';
import { UntypedFormControl, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { IGenerate2FAResponse, ITOTPResponse, IUser } from '../../../types/user';
import { UserService } from '../../../services/user-service';
import { TranslateService } from '@ngx-translate/core';
import { debounceTime } from 'rxjs/operators';
import multiDownload from 'multi-download';
import { BehaviorSubject } from 'rxjs';

@Component({
    templateUrl: `./twofactor.component.html`,
    styleUrls: ['twofactor.component.scss'],
    standalone: false
})
export class GoogleAuthDialogComponent implements OnInit {
  totpCode: UntypedFormControl;
  qrcode = '';
  key = '';
  keydesc: Boolean;
  showBarCode = true;
  invalidToTP = false;
  totpCheckFailed = false;
  showRecoveryCode = false;
  recoveryCode: string;
  recoveryCodeFile: string;
  verifying$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  // Translate strings
  tfsTitleText: string;
  tfsStepOne: string;
  tfsStepTwo: string;
  tfsStepThree: string;
  tfsStepThreeManual: string;
  tfsStepFour: string;
  tfsNoScan: string;
  tfsScan: string;
  tfsAccountName: string;
  tfsSecretKey: string;
  tfsSpaces: string;
  tfsOtpCode: string;
  tfsInvalidOtpCode: string;
  tfsFailedOtpCode: string;
  tfsRecoveryTextOne: string;
  tfsRecoveryTextTwo: string;
  tfsRecoveryDownload: string;
  tfsRecoveryTextThree: string;
  tfsDone: string;
  tfsCancel: string;
  tfsNext: string;

  constructor(public dialogRef: MatDialogRef<GoogleAuthDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: {user: IUser},
    private userSvc: UserService,
    private translate: TranslateService) {
    this.translate.get(['TFS_TITLE', 'TFS_STEP_ONE', 'TFS_STEP_TWO', 'TFS_STEP_THREE', 'TFS_STEP_THREE_MANUAL',
      'TFS_STEP_FOUR', 'TFS_NO_SCAN', 'TFS_SCAN', 'TFS_ACCOUNT_NAME', 'TFS_SECRET_KEY', 'TFS_SPACES', "TFS_OTP_CODE",
        'TFS_INVALID_OTP_CODE', 'TFS_FAILED_OTP_CODE', 'TFS_RECOVERY_TEXT_ONE', 'TFS_RECOVERY_TEXT_TWO',
        'TFS_RECOVERY_DOWNLOAD', 'TFS_RECOVERY_TEXT_THREE', 'DONE', 'CANCEL', 'NEXT'])
        .subscribe(vals => {
            this.tfsTitleText = vals['TFS_TITLE'];
            this.tfsStepOne = vals["TFS_STEP_ONE"];
            this.tfsStepTwo = vals["TFS_STEP_TWO"];
            this.tfsStepThree = vals["TFS_STEP_THREE"];
            this.tfsStepThreeManual = vals["TFS_STEP_THREE_MANUAL"];
            this.tfsStepFour = vals["TFS_STEP_FOUR"];
            this.tfsNoScan = vals["TFS_NO_SCAN"];
            this.tfsScan = vals["TFS_SCAN"];
            this.tfsAccountName = vals["TFS_ACCOUNT_NAME"];
            this.tfsSecretKey = vals["TFS_SECRET_KEY"];
            this.tfsSpaces = vals["TFS_SPACES"];
            this.tfsOtpCode = vals["TFS_OTP_CODE"];
            this.tfsInvalidOtpCode = vals["TFS_INVALID_OTP_CODE"];
            this.tfsFailedOtpCode = vals["TFS_FAILED_OTP_CODE"];
            this.tfsRecoveryTextOne = vals["TFS_RECOVERY_TEXT_ONE"];
            this.tfsRecoveryTextTwo = vals["TFS_RECOVERY_TEXT_TWO"];
            this.tfsRecoveryDownload = vals["TFS_RECOVERY_DOWNLOAD"];
            this.tfsRecoveryTextThree = vals["TFS_RECOVERY_TEXT_THREE"];
            this.tfsDone = vals["DONE"];
            this.tfsCancel = vals["CANCEL"];
            this.tfsNext = vals["NEXT"];
    });
        dialogRef.disableClose = true;
        this.totpCode = new UntypedFormControl(this.totpCode, [
          Validators.required,
          Validators.minLength(6),
          Validators.maxLength(6),
        ]);
    }

  ngOnInit(): void {
    this.registerTwoFactor();
    this.totpCode.valueChanges.subscribe(value => {
      const val = value.toString().trim();
      if (val && !isNaN(+val) && val.length === 6) {
        this.invalidToTP = false;
        this.totpCheckFailed = false;
      }
    });
  }

  getFormattedSecretKey(key) {
    return key.match(/.{1,4}/g).join(' ');
  }

  onCancel() {
    this.dialogRef.close(false);
  }

  onDoneClick() {
    this.dialogRef.close(true);
  }

  onDownloadRecoveryCodeFile() {
    multiDownload([this.recoveryCodeFile], {
      rename: () => 'cigent-central-console-2fa-recovery-code.txt'
    });
  }

  public registerTwoFactor() {
    this.userSvc.getTwoFactor().subscribe(
      (result: IGenerate2FAResponse) => {
        this.qrcode = result.qr_code;
        this.key = result.key;
      },
      () => {
        this.qrcode = '';
        this.key = '';
      }
    );
  }

  public addVerifyTotp() {
    const totpCode = this.totpCode.value && this.totpCode.value.trim();
    this.invalidToTP = false;
    if (!totpCode || isNaN(+totpCode) || totpCode.length !== 6) {
      this.totpCheckFailed = false;
      this.invalidToTP = true;
      return;
    }
    this.verifying$.next(true);
    this.userSvc.addVerifyTotp(totpCode)
      .pipe(
        debounceTime(500)
      )
      .subscribe({
          next: (response: ITOTPResponse) => {
            if (response.totpCodeIsValid) {
              const {recoveryCode} = response;
              this.recoveryCode = recoveryCode;
              this.recoveryCodeFile = URL.createObjectURL(new Blob([recoveryCode], {type: 'text/plain'}));
              this.showRecoveryCode = true;
              this.invalidToTP = false;
              this.totpCheckFailed = false;
              // this.dialogRef.close();
            } else {
              this.invalidToTP = false;
              this.totpCheckFailed = true;
            }
          },
          error: () => {
            this.verifying$.next(false)
          },
          complete: () => {
            this.verifying$.next(false)
          }
        }
      );
  }
}
